import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { X } from 'lucide-react'
import { Button, Input, Textarea, Select, Label } from '@/components/ui'
import { useUpsertApp } from '@/hooks/useApps'
import { ENTITIES, STATUSES, CATEGORIES, slugify } from '@/lib/utils'
import type { AppRecord } from '@/lib/types'
import { toast } from 'sonner'

const EMPTY: AppRecord = {
  slug: '', name: '', path: '', category: 'other', entity: 'unknown',
  stack: [], description: '', status: 'wip', github_repo: null, firebase_project: null,
  firebase_site: null, last_modified: new Date().toISOString(), loc_estimate: 0,
  complexity: 'simple', revenue_model_hint: 'unclear', key_features: [], notes: '',
}

export function AppFormDialog({ open, onClose, initial }: {
  open: boolean
  onClose: () => void
  initial?: AppRecord
}) {
  const [form, setForm] = useState<AppRecord>(initial ?? EMPTY)
  const upsert = useUpsertApp()

  useEffect(() => {
    if (open) setForm(initial ?? EMPTY)
  }, [open, initial])

  function update<K extends keyof AppRecord>(k: K, v: AppRecord[K]) {
    setForm((s) => ({ ...s, [k]: v }))
  }

  async function handleSave() {
    if (!form.name.trim()) return toast.error('Name is required')
    const payload: AppRecord = {
      ...form,
      slug: form.slug || slugify(form.name),
      stack: typeof form.stack === 'string' ? (form.stack as string).split(',').map((s) => s.trim()).filter(Boolean) : form.stack,
      key_features: typeof form.key_features === 'string' ? (form.key_features as string).split('\n').map((s) => s.trim()).filter(Boolean) : form.key_features,
    }
    try {
      await upsert.mutateAsync(payload)
      toast.success(initial ? 'App updated' : 'App created')
      onClose()
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-navy-deep/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="glass-warm rounded-xl max-w-2xl w-full max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-8 py-6 border-b border-bronze/30 flex items-center justify-between">
              <div>
                <div className="eyebrow !text-bronze-deep mb-1">{initial ? 'Edit App' : 'New App'}</div>
                <h2 className="font-heading text-2xl text-navy">{initial ? form.name : 'Add a new app to the portfolio'}</h2>
              </div>
              <button onClick={onClose} className="text-navy/55 hover:text-navy"><X size={20} /></button>
            </div>

            <div className="px-8 py-6 space-y-4 text-navy">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="!text-bronze-deep">Name</Label>
                  <Input value={form.name} onChange={(e) => update('name', e.target.value)} className="!bg-cream !text-navy !border-bronze/40" />
                </div>
                <div>
                  <Label className="!text-bronze-deep">Slug</Label>
                  <Input value={form.slug} onChange={(e) => update('slug', e.target.value)} placeholder={slugify(form.name)} className="!bg-cream !text-navy !border-bronze/40 !font-mono !text-xs" />
                </div>
              </div>

              <div>
                <Label className="!text-bronze-deep">Description</Label>
                <Textarea value={form.description} onChange={(e) => update('description', e.target.value)} className="!bg-cream !text-navy !border-bronze/40 min-h-20" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="!text-bronze-deep">Entity</Label>
                  <Select value={form.entity} onChange={(e) => update('entity', e.target.value as AppRecord['entity'])} className="!bg-cream !text-navy !border-bronze/40">
                    {ENTITIES.map((e) => <option key={e}>{e}</option>)}
                  </Select>
                </div>
                <div>
                  <Label className="!text-bronze-deep">Status</Label>
                  <Select value={form.status} onChange={(e) => update('status', e.target.value as AppRecord['status'])} className="!bg-cream !text-navy !border-bronze/40">
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </Select>
                </div>
                <div>
                  <Label className="!text-bronze-deep">Category</Label>
                  <Select value={form.category} onChange={(e) => update('category', e.target.value as AppRecord['category'])} className="!bg-cream !text-navy !border-bronze/40">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="!text-bronze-deep">Complexity</Label>
                  <Select value={form.complexity} onChange={(e) => update('complexity', e.target.value as AppRecord['complexity'])} className="!bg-cream !text-navy !border-bronze/40">
                    {['simple','moderate','complex','enterprise'].map((c) => <option key={c}>{c}</option>)}
                  </Select>
                </div>
                <div>
                  <Label className="!text-bronze-deep">Revenue model</Label>
                  <Select value={form.revenue_model_hint} onChange={(e) => update('revenue_model_hint', e.target.value as AppRecord['revenue_model_hint'])} className="!bg-cream !text-navy !border-bronze/40">
                    {['subscription','one-time','acquisition-target','internal-only','open-source','unclear'].map((r) => <option key={r}>{r}</option>)}
                  </Select>
                </div>
              </div>

              <div>
                <Label className="!text-bronze-deep">Stack (comma-separated)</Label>
                <Input value={Array.isArray(form.stack) ? form.stack.join(', ') : form.stack as unknown as string} onChange={(e) => update('stack', e.target.value as unknown as string[])} className="!bg-cream !text-navy !border-bronze/40" />
              </div>

              <div>
                <Label className="!text-bronze-deep">Key features (one per line)</Label>
                <Textarea value={Array.isArray(form.key_features) ? form.key_features.join('\n') : form.key_features as unknown as string} onChange={(e) => update('key_features', e.target.value as unknown as string[])} className="!bg-cream !text-navy !border-bronze/40 min-h-24" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="!text-bronze-deep">GitHub repo</Label>
                  <Input value={form.github_repo ?? ''} onChange={(e) => update('github_repo', e.target.value || null)} className="!bg-cream !text-navy !border-bronze/40" />
                </div>
                <div>
                  <Label className="!text-bronze-deep">Firebase site</Label>
                  <Input value={form.firebase_site ?? ''} onChange={(e) => update('firebase_site', e.target.value || null)} className="!bg-cream !text-navy !border-bronze/40" />
                </div>
              </div>

              <div>
                <Label className="!text-bronze-deep">Local path</Label>
                <Input value={form.path} onChange={(e) => update('path', e.target.value)} className="!bg-cream !text-navy !border-bronze/40 !font-mono !text-xs" />
              </div>

              <div>
                <Label className="!text-bronze-deep">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} className="!bg-cream !text-navy !border-bronze/40" />
              </div>
            </div>

            <div className="px-8 py-5 border-t border-bronze/30 flex items-center justify-end gap-2">
              <Button variant="quiet" onClick={onClose}>Cancel</Button>
              <Button onClick={handleSave} loading={upsert.isPending}>
                {initial ? 'Save changes' : 'Create app'}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
